// ==================== SiteConfiguration.jsx ====================
// This version includes full Step 1 + Step 2 logic with explanatory comments
// Author: ChatGPT (GPT-5) - Production Ready Version
// ===============================================================

import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardHeader, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import clsx from "clsx";
import "./SiteConfiguration.css";

// === API Imports ===
import {
  listPVModulesBySite,
  createPVModule,
  updatePVModule,
  deletePVModule,
  canDeletePVModule,
} from "../api/pvmodules";
import {
  listInvertersBySite,
  createInverter,
  updateInverter,
  deleteInverter,
  canDeleteInverter,
} from "../api/inverters";
import {
  listTransformersBySite,
  createTransformer,
  updateTransformer,
  deleteTransformer,
  canDeleteTransformer,
} from "../api/transformers";
import {
  listArraysBySite,
  listArraysDetailedBySite,
  createArray,
  updateArray,
  deleteArray,
} from "../api/arrays";
import {
  listArrayInvertersDetailedBySite,
  createArrayInverter,
  updateArrayInverter,
  deleteArrayInverter,
} from "../api/arrayinverters";
import {
  createArrayModule,
  updateArrayModule,
  deleteArrayModule,
 } from "../api/arraymodules";
import { recalcSiteCapacity } from "../api/sites";

const STEP1_NUMERIC_FIELDS = {
  module: ["power_wp", "module_length", "module_width", "module_tempcoeff_pmax", "module_efficiency"],
  inverter: ["ac_power", "efficiency", "inv_status1_boot"],
  transformer: ["nominal_power", "iron_loss", "load_loss"],
};

const parseOptionalNumber = (value) => {
  if (value === "" || value === undefined || value === null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export default function SiteConfiguration() {
  const { siteId } = useParams();
  const navigate = useNavigate();

  // ---------- Global state ----------
  const [step, setStep] = useState(1);
  const [finishLoading, setFinishLoading] = useState(false);

  // --- STEP 1 data ---
  const [modules, setModules] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [transformers, setTransformers] = useState([]);

  // --- STEP 2 data ---
  const [arrays, setArrays] = useState([]);
  const [arraysDetailed, setArraysDetailed] = useState([]);
  const [invArrayConfigs, setInvArrayConfigs] = useState([]);

  // --- Selection (for Step 2 editable tables) ---
  const [selectedArray, setSelectedArray] = useState(null);
  const [selectedInvArray, setSelectedInvArray] = useState(null);

  // --- Pop-up & warning state ---
  const [open, setOpen] = useState(false);
  const [formType, setFormType] = useState(null);
  const [formData, setFormData] = useState({});
  const [warning, setWarning] = useState(null);

  const initialFocusRef = useRef(null);
  const fieldRefs = useRef({});
  const activeFieldNameRef = useRef(null);

  useEffect(() => {
    if (!open) {
      activeFieldNameRef.current = null;
      fieldRefs.current = {};
      return;
    }

    queueMicrotask(() => {
      const node = initialFocusRef.current;
      if (node) {
        node.focus();
        activeFieldNameRef.current = node.name || null;
      }
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const activeName = activeFieldNameRef.current;
    if (!activeName) return;
    const node = fieldRefs.current[activeName];
    if (!node || !node.isConnected || document.activeElement === node) return;
    try {
      node.focus();
      if (node.setSelectionRange && typeof node.value === "string") {
        const len = node.value.length;
        node.setSelectionRange(len, len);
      }
    } catch (err) {
      console.warn("Failed to restore focus", err);
    }
  }, [formData, open]);

  // ===============================================================
  // INITIAL LOAD
  // ===============================================================
  useEffect(() => {
    if (!siteId) return;
    if (step === 1) {
      loadStep1();
    } else if (step === 2) {
      loadStep2();
    }
  }, [siteId, step]);

  // Load Step 1 data (main equipment)
  const loadStep1 = async () => {
    try {
      const [m, i, t] = await Promise.all([
        listPVModulesBySite(siteId),
        listInvertersBySite(siteId),
        listTransformersBySite(siteId),
      ]);
      setModules(m || []);
      setInverters(i || []);
      setTransformers(t || []);
    } catch (err) {
      console.error("Error loading equipment:", err);
    }
  };

    // Load Step 2 data (arrays)
  const loadStep2 = async () => {
    try {
      const [arrRaw, arrDet, invDet] = await Promise.all([
        listArraysBySite(siteId),
        listArraysDetailedBySite(siteId),
        listArrayInvertersDetailedBySite(siteId),
      ]);

      // --- Sort Transformer Array table (ascending by Array Name) ---
      const sortedArrays = (arrDet || []).sort((a, b) =>
        a.array_name.localeCompare(b.array_name, undefined, { numeric: true, sensitivity: "base" })
      );

      // --- Sort Inverter Array table (by Array Name, then Inverter Array Name) ---
      const sortedInvArrays = (invDet || []).sort((a, b) => {
        const nameCompare = a.array_name.localeCompare(b.array_name, undefined, { numeric: true, sensitivity: "base" });
        return nameCompare !== 0
          ? nameCompare
          : a.inv_array_name.localeCompare(b.inv_array_name, undefined, { numeric: true, sensitivity: "base" });
      });

      setArrays(arrRaw || []);
      setArraysDetailed(sortedArrays);
      setInvArrayConfigs(sortedInvArrays);
    } catch (err) {
      console.error("Error loading array configuration:", err);
    }
  };

  // ===============================================================
  // GENERIC HANDLERS
  // ===============================================================
  const handleAdd = (type, data = {}) => {
    setFormType(type);
    setFormData(data);
    setOpen(true);
  };

  const handleEdit = (type, record) => {
    if (!record) return;

    let editableRecord = { ...record };

    if (type === "array") {
      // enrich transformer_id when only transformer_name is present
      if (editableRecord.transformer_name && transformers.length) {
        const match = transformers.find((t) => t.name === editableRecord.transformer_name);
        if (match) editableRecord.transformer_id = match.id;
      }
    }

    if (type === "arrayInverter") {
      editableRecord = {
        id: record.id,
        array_id: record.array_id ? String(record.array_id) : "",
        inv_array_name: record.inv_array_name ?? "",
        inverter_id: record.inverter_id ? String(record.inverter_id) : "",
        module_id: record.module_id ? String(record.module_id) : "",
        quantity: record.quantity ?? record.inverter_qty ?? "",
        array_module_id: record.array_module_id ?? null,
      };
    }

    setFormType(type);
    setFormData(editableRecord);
    setOpen(true);
  };



  // ===============================================================
  // SAFE DELETE HANDLER (Step 1)
  // ===============================================================
  const handleDeleteSafe = async (type, id) => {
    try {
      let canDelete = true;
      if (type === "module") {
        const { can_delete } = await canDeletePVModule(id);
        canDelete = can_delete;
      }
      if (type === "inverter") {
        const { can_delete } = await canDeleteInverter(id);
        canDelete = can_delete;
      }
      if (type === "transformer") {
        const { can_delete } = await canDeleteTransformer(id);
        canDelete = can_delete;
      }

      // Show warning if record is in use
      if (!canDelete) {
        setWarning("⚠️ Cannot delete – this item is already used in Step 2 configuration.");
        return;
      }

      // Safe to delete
      if (type === "module") await deletePVModule(id);
      if (type === "inverter") await deleteInverter(id);
      if (type === "transformer") await deleteTransformer(id);

      await loadStep1();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // ===============================================================
  // SAVE HANDLER (CREATE + UPDATE for all forms)
  // ===============================================================
  const handleSave = async () => {
    try {
      // --- Step 1 equipment ---
      if (["module", "inverter", "transformer"].includes(formType)) {
        const { id, ...rest } = formData;
        const payload = { ...rest, site_id: Number(siteId) };
        const numericKeys = STEP1_NUMERIC_FIELDS[formType] ?? [];

        numericKeys.forEach((key) => {
          if (payload[key] === "" || payload[key] === undefined || payload[key] === null) {
            delete payload[key];
            return;
          }
          const parsed = Number(payload[key]);
          if (!Number.isNaN(parsed)) {
            payload[key] = parsed;
          }
        });

        if (id) {
          if (formType === "module") await updatePVModule(id, payload);
          if (formType === "inverter") await updateInverter(id, payload);
          if (formType === "transformer") await updateTransformer(id, payload);
        } else {
          if (formType === "module") await createPVModule(payload);
          if (formType === "inverter") await createInverter(payload);
          if (formType === "transformer") await createTransformer(payload);
        }

        await loadStep1();

        // ✅ refresh Step 2 if a transformer (array) was added or renamed
        if (formType === "transformer") {
          await loadStep2();
        }
      }

      setOpen(false);
    } catch (err) {
      console.error("Save error:", err);
    }
  };


  // ==========================================================
  // REUSABLE TABLE RENDERER FOR STEP 1 (with scroll + sticky header)
  // ==========================================================
  const renderStep1Table = (title, data, fields, type, onEdit, onDelete) => (
    <Card className="mb-6">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b bg-slate-50">
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500">{data.length} item{data.length === 1 ? "" : "s"}</p>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => handleAdd(type)}>
          + Add
        </Button>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                {fields.map((f) => (
                  <th key={f.key} className="text-left px-4 py-3 font-semibold">
                    {f.label}
                  </th>
                ))}
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan={fields.length + 1} className="px-4 py-6 text-center text-slate-500">
                    No records yet. Click “Add” to create one.
                  </td>
                </tr>
              )}

              {data.map((row) => (
                <tr key={row.id} className="border-t hover:bg-slate-50">
                  {fields.map((f) => (
                    <td key={f.key} className="px-4 py-3 text-slate-800">
                      <div className="font-medium">{row[f.key] ?? "—"}</div>
                      {f.showId && (
                        <div className="text-xs text-slate-500">ID {row.id}</div>
                      )}
                    </td>
                  ))}

                  <td className="px-4 py-3 text-right space-x-3">
                    <button
                      className="text-blue-600 hover:underline text-sm font-medium"
                      onClick={() => onEdit(type, row)}
                    >
                      Edit
                    </button>
                    <button
                      className="text-rose-600 hover:underline text-sm font-medium"
                      onClick={() => onDelete(type, row.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  // ===============================================================
  // STEP 1 PAGE CONTENT
  // ===============================================================
  const renderStep1 = () => (
    <>
      {renderStep1Table(
        "PV Modules",
        modules,
        [
          { key: "id", label: "ID" },
          { key: "name", label: "Module Type" },
          { key: "power_wp", label: "Power (Wp)" },
          { key: "module_efficiency", label: "Efficiency (%)" },
        ],
        "module",
        handleEdit,
        handleDeleteSafe
      )}

      {renderStep1Table(
        "Inverters",
        inverters,
        [
          { key: "id", label: "ID" },
          { key: "name", label: "Inverter Type" },
          { key: "ac_power", label: "Power (kVA)" },
          { key: "efficiency", label: "Inverter eff. (%)" },
        ],
        "inverter",
        handleEdit,
        handleDeleteSafe
      )}

      {renderStep1Table(
        "Transformers",
        transformers,
        [
          { key: "id", label: "ID" },
          { key: "name", label: "Transformer Type" },
          { key: "nominal_power", label: "Power (VA)" },
          { key: "iron_loss", label: "Iron loss (W)" },
          { key: "load_loss", label: "Load loss (w)" },
        ],
        "transformer",
        handleEdit,
        handleDeleteSafe
      )}

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => navigate("/maintenance/sites")}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            setStep(2);
            loadStep2();
          }}
        >
          Next
        </Button>
      </div>
    </>
  );

  // ===============================================================
  // (Step 2 + popup forms continue below)
  // ===============================================================

  // ===============================================================
  // STEP 2 HANDLERS
  // ===============================================================

  // --- Add Transformer Array ---
  const handleAddTransformerArray = () => {
    setFormType("array");
    setFormData({ array_name: "", transformer_id: "", row_pitch_m: "", collector_width_m: "" });
    setOpen(true);
  };

  // --- Add Inverter Array ---
  const handleAddInverterArray = () => {
    const last = invArrayConfigs[invArrayConfigs.length - 1];
    setFormType("arrayInverter");
    setFormData(
      last
        ? {
            array_id: last.array_id ?? "",
            inv_array_name: "",
            inverter_id: last.inverter_id ?? "",
            module_id: last.module_id ?? "",
            quantity: last.quantity ?? last.inverter_qty ?? "",
            array_module_id: null,
          }
        : { array_id: "", inv_array_name: "", inverter_id: "", module_id: "", quantity: "", array_module_id: null }
    );
    setOpen(true);
  };

  // --- Delete handlers for Step 2 ---
  const handleDeleteArray = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transformer array?")) return;

    try {
      await deleteArray(id);
      await loadStep2();
      setSelectedArray(null);
    } catch (err) {
      console.error("Delete transformer array failed:", err);

      const backendMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "Unknown error";

      alert(`❌ Cannot delete this array:\n${backendMsg}`);
    }
  };

  const handleDeleteInverterArray = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inverter array?")) return;

    try {
      await deleteArrayInverter(id);
      await loadStep2();
      setSelectedInvArray(null);
    } catch (err) {
      console.error("Delete inverter array failed:", err);

      const backendMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "Unknown error";

      alert(`❌ Cannot delete this inverter array:\n${backendMsg}`);
    }
  };

  // ===============================================================
  // EXTEND SAVE HANDLER (Step 2 logic)
  // ===============================================================
  // =============================================================
  // --- Save Step 2 data (Transformer Array / Inverter Array) ---
  // =============================================================
  const saveStep2Data = async () => {
    try {
      if (!siteId) {
        alert("❌ Site ID missing – cannot save record.");
        return;
      }

     if (formType === "array") {
      const payload = {
        site_id: Number(siteId),
        transformer_id: Number(formData.transformer_id),
        array_name: formData.array_name,
        row_pitch_m: parseOptionalNumber(formData.row_pitch_m),
        collector_width_m: parseOptionalNumber(formData.collector_width_m),
      };

      console.log("Saving Array:", payload);

      // ✅ Decide between create or update
      if (formData.id) {
        await updateArray(formData.id, payload); // PUT /arrays/{id}
      } else {
        await createArray(payload); // POST /arrays
      }
    }

      if (formType === "arrayInverter") {
        // Step 1: create ArrayInverter
        const moduleQuantity = Number(formData.quantity || 1);

        const invPayload = {
          array_id: Number(formData.array_id),
          inverter_id: Number(formData.inverter_id),
          inverter_qty: 1,
          inv_array_name: formData.inv_array_name
        };

        let ai;
        if (formData.id) {
          ai = await updateArrayInverter(formData.id, invPayload);
        } else {
          ai = await createArrayInverter(invPayload);
        }

        const invArrayId = ai?.id ?? ai?.data?.id ?? formData.id;

        // Step 2: link PV module if selected
        if (formData.module_id && invArrayId) {
          const modPayload = {
            inv_array_id: invArrayId,
            module_id: Number(formData.module_id),
            quantity: moduleQuantity
          };

          if (formData.array_module_id) {
            await updateArrayModule(formData.array_module_id, modPayload);
          } else {
            await createArrayModule(modPayload); // POST /arraymodules
          }
        } else if (formData.array_module_id) {
          await deleteArrayModule(formData.array_module_id);
        }
      }

      // refresh data and close popup
      await loadStep2();
      setOpen(false);
    } catch (err) {
      console.error("❌ Save Step 2 failed:", err);
      alert(`Save failed: ${err?.message || err}`);
    }
  };


  // ===============================================================
  // RENDER STEP 2 TABLES
  // ===============================================================

  const handleSelectArray = (a) =>
  setSelectedArray(prev => (prev?.id === a.id ? null : a));

  const handleSelectInvArray = (i) =>
    setSelectedInvArray(prev => (prev?.id === i.id ? null : i))

  const renderStep2 = () => (
    <>
      {/* --- Transformer Arrays --- */}
      <Card className="mb-6">
        <CardHeader className="flex justify-between">
          <h3 className="font-semibold">Transformer Array Configuration</h3>
          <div className="flex gap-2">
            <Button onClick={handleAddTransformerArray}>Add</Button>
            <Button
              variant="secondary"
              disabled={!selectedArray}
              onClick={() => handleEdit("array", selectedArray)}
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              disabled={!selectedArray}
              onClick={() => handleDeleteArray(selectedArray.id)}
            >
              Delete
            </Button>
          </div>
        </CardHeader>

        {/* Scrollable table container */}
        <CardContent className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full border text-sm">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="border px-2 py-1 text-left w-16">ID</th>
                <th className="border px-2 py-1 text-left">Array Name</th>
                <th className="border px-2 py-1 text-left">Transformer Type</th>
                <th className="border px-2 py-1 text-left">Row Pitch Override (m)</th>
                <th className="border px-2 py-1 text-left">Collector Width Override (m)</th>
              </tr>
            </thead>
            <tbody>
              {arraysDetailed.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => handleSelectArray(a)}
                  className={clsx(
                    "cursor-pointer hover:bg-blue-50",
                    selectedArray?.id === a.id && "bg-blue-100"
                  )}
                >
                  <td className="border px-2 py-1 text-gray-600">{a.id}</td>
                  <td className="border px-2 py-1">{a.array_name}</td>
                  <td className="border px-2 py-1">{a.transformer_name}</td>
                  <td className="border px-2 py-1">{a.row_pitch_m ?? "—"}</td>
                  <td className="border px-2 py-1">{a.collector_width_m ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* --- Inverter Arrays --- */}
      <Card>
        <CardHeader className="flex justify-between">
          <h3 className="font-semibold">Inverter Array Configuration</h3>
          <div className="flex gap-2">
            <Button onClick={handleAddInverterArray}>Add</Button>
            <Button
              variant="secondary"
              disabled={!selectedInvArray}
              onClick={() => handleEdit("arrayInverter", selectedInvArray)}
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              disabled={!selectedInvArray}
              onClick={() => handleDeleteInverterArray(selectedInvArray.id)}
            >
              Delete
            </Button>
          </div>
        </CardHeader>

        {/* Scrollable table container */}
        <CardContent className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full border text-sm">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="border px-2 py-1 text-left">Array Name</th>
                <th className="border px-2 py-1 text-left">Inverter Array Name</th>
                <th className="border px-2 py-1 text-left">Inverter Type</th>
                <th className="border px-2 py-1 text-left">Module Type</th>
                <th className="border px-2 py-1 text-left">Qty</th>
              </tr>
            </thead>
            <tbody>
              {invArrayConfigs.map((i) => (
                <tr
                  key={i.id}
                  onClick={() => handleSelectInvArray(i)}
                  className={clsx(
                    "cursor-pointer hover:bg-blue-50",
                    selectedInvArray?.id === i.id && "bg-blue-100"
                  )}
                >
                  <td className="border px-2 py-1">{i.array_name}</td>
                  <td className="border px-2 py-1">{i.inv_array_name}</td>
                  <td className="border px-2 py-1">{i.inverter_name}</td>
                  <td className="border px-2 py-1">{i.module_name}</td>
                  <td className="border px-2 py-1 text-center">{i.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => setStep(1)}>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={async () => {
            if (!siteId) {
              navigate("/maintenance/sites");
              return;
            }
            setFinishLoading(true);
            try {
              await recalcSiteCapacity(Number(siteId));
              navigate("/maintenance/sites");
            } catch (err) {
              console.error("Failed to recalculate site capacity", err);
              alert("Failed to recalculate site capacity. Please try again.");
            } finally {
              setFinishLoading(false);
            }
          }}
          disabled={finishLoading}
        >
          {finishLoading ? "Saving…" : "Finish"}
        </Button>
      </div>
    </>
  );

  // ===============================================================
  // POP-UP FORM RENDERING (two-column fixed-height layout)
  // ===============================================================
  // --- Common reusable field generator (no autofocus!) ---
  const renderFormFields = () => {
    const arrayOptions = (arraysDetailed || []).map(a => ({ id: a.id, name: a.array_name }));
    const inverterOptions = (inverters || []).map(i => ({ id: i.id, name: i.name }));
    const moduleOptions = (modules || []).map(m => ({ id: m.id, name: m.name }));

    const Field = ({ label, name, type = "text", options = [], inputRef }) => {
      const assignRef = (node) => {
        if (node) {
          fieldRefs.current[name] = node;
          if (inputRef) {
            inputRef.current = node;
          }
        } else {
          delete fieldRefs.current[name];
          if (inputRef) {
            inputRef.current = null;
          }
        }
      };

      const handleFocus = () => {
        activeFieldNameRef.current = name;
      };

      const handleChange = (value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
      };

      return (
        <div className="flex flex-col w-1/2 p-2">
          <label className="text-xs mb-1 text-gray-600">{label}</label>
          {options.length > 0 ? (
            <select
              name={name}
              value={String(formData[name] ?? "")}
              onChange={(e) => handleChange(e.target.value)}
              onFocus={handleFocus}
              ref={assignRef}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="">Select...</option>
              {options.map((opt) => (
                <option key={opt.id} value={String(opt.id)}>
                  {opt.name}
                </option>
              ))}
            </select>
          ) : (
            <Input
              name={name}
              type={type}
              value={formData[name] ?? ""}
              onChange={(e) => handleChange(e.target.value)}
              onFocus={handleFocus}
              ref={assignRef}
            />
          )}
        </div>
      );
    };

    if (!formType) return null;

    // --- Step 1: Main equipment forms ---
    if (["module", "inverter", "transformer"].includes(formType)) {
      return (
        <div className="flex flex-wrap">
          <Field label="Name" name="name" inputRef={initialFocusRef} />
          {formType === "module" && (
            <>
              <Field label="Power (Wp)" name="power_wp" type="number" />
              <Field label="Length (m)" name="module_length" type="number" />
              <Field label="Width (m)" name="module_width" type="number" />
              <Field label="Efficiency (%)" name="module_efficiency" type="number" />
              <Field label="Temp Coeff. (%/°C)" name="module_tempcoeff_pmax" type="number" />
            </>
          )}
          {formType === "inverter" && (
            <>
              <Field label="Power (kVA)" name="ac_power" type="number" />
              <Field label="Efficiency (%)" name="efficiency" type="number" />
              <Field label="STATUS1 boot code" name="inv_status1_boot" type="number" />
            </>
          )}
          {formType === "transformer" && (
            <>
              <Field label="Nominal Power (VA)" name="nominal_power" type="number" />
              <Field label="No Load Loss (W)" name="iron_loss" type="number" />
              <Field label="Load Loss (W)" name="load_loss" type="number" />
            </>
          )}
        </div>
      );
    }

    // --- Step 2: Transformer Array form ---
    if (formType === "array") {
      return (
        <div className="flex flex-wrap">
          <Field label="Array Name" name="array_name" inputRef={initialFocusRef} />
          <Field label="Transformer Type" name="transformer_id" options={transformers} />
          <Field label="Row Pitch Override (m)" name="row_pitch_m" type="number" />
          <Field label="Collector Width Override (m)" name="collector_width_m" type="number" />
          <div className="w-full px-2 pt-1 text-xs text-slate-500">
            Leave override fields blank to inherit the site defaults. Use meters, for example `2.384`, not `2384`.
          </div>
        </div>
      );
    }

    // --- Step 2: Inverter Array form ---
    if (formType === "arrayInverter") {
      return (
        <div className="flex flex-wrap">
          <Field label="Array" name="array_id" options={arrayOptions} />
          <Field label="Inverter Array Name" name="inv_array_name" inputRef={initialFocusRef} />
          <Field label="Inverter Type" name="inverter_id" options={inverterOptions} />
          <Field label="Module Type" name="module_id" options={moduleOptions} />
          <Field label="Module Quantity" name="quantity" type="number" />
        </div>
      );
    }

    // --- Fallback ---
    return <p className="text-gray-500">Invalid form type.</p>;
  };

  // ===============================================================
  // FINAL RENDER
  // ===============================================================
  const memoizedRenderFields = React.useMemo(
    () => renderFormFields(),
    [formType, formData, arraysDetailed, inverters, modules, transformers]
  );

  return (
    <div className="p-6">
      {/* Step Indicator */}
      <div className="flex items-center mb-6">
        <div className={clsx(
          "px-4 py-2 rounded-l-lg border",
          step === 1 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
        )}>
          Step 1 – Equipment Configuration
        </div>
        <div className={clsx(
          "px-4 py-2 rounded-r-lg border-t border-b border-r",
          step === 2 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
        )}>
          Step 2 – Array Configuration
        </div>
      </div>
     
      <h2 className="text-xl font-semibold mb-4">Site Configuration</h2>

      {step === 1 ? renderStep1() : renderStep2()}

      {/* --- Pop-up Dialog --- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl"
          forceMount
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="capitalize">
              {formData.id ? `Edit ${formType}` : `Add ${formType}`}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-2">{memoizedRenderFields}</div>

          <div className="flex justify-end mt-4 gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                ["array", "arrayInverter"].includes(formType)
                  ? saveStep2Data()
                  : handleSave()
              }
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- Warning Dialog --- */}
      <Dialog open={!!warning} onOpenChange={() => setWarning(null)}>
        <DialogContent
          className="max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Warning</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 mt-2">{warning}</p>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setWarning(null)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
